import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay
} from '@chakra-ui/react';
import QueuePriorityOrderView from './QueuePriorityOrderView';
import { QueuePriorityHeaderActionsContext } from './queue-priority-header-actions-context';
import { closeQueuePriorityModal } from '../../actions/queue-priority-modal.action';
import { selectQueuePriorityModal } from '../../reducers/queue-priority-modal.reducer';
import { selectHasUnsavedPriorityOrder } from '../../reducers/queue-priority-order.reducer';
import { selectCanPrioritize } from '../../core/prioritize-access';

export const PRIORITIZE_WORK_MODAL_TITLE = 'Prioritize Work';

/**
 * The authoritative resource gate, not just the trigger — see `core/prioritize-access.ts`.
 */
export const QueuePriorityModalContainer: React.FC = () => {
  const dispatch = useDispatch();
  const { isOpen } = useSelector(selectQueuePriorityModal);
  const canPrioritize = useSelector(selectCanPrioritize);
  const hasUnsavedOrder = useSelector(selectHasUnsavedPriorityOrder);
  const tenantId = useSelector(
    (state: any) => state.ui?.context?.activeContext?.tenantId as string | undefined
  );
  const serviceLine = useSelector(
    (state: any) => state.ui?.serviceLine?.name as string | undefined
  );

  // Portal target for QueuePriorityOrderView's Revert/Save buttons, so they render
  // here in the header row (left of the close button) instead of the view's own header.
  const [headerActionsNode, setHeaderActionsNode] = useState<HTMLDivElement | null>(null);

  const handleClose = useCallback(() => {
    dispatch(closeQueuePriorityModal());
  }, [dispatch]);

  useEffect(() => {
    if (isOpen && !canPrioritize) {
      dispatch(closeQueuePriorityModal());
    }
  }, [isOpen, canPrioritize, dispatch]);

  const previousContext = useRef<string | null>(null);
  useEffect(() => {
    const contextKey = `${tenantId ?? ''}::${serviceLine ?? ''}`;
    const previous = previousContext.current;
    previousContext.current = contextKey;
    // First run must not close: that would dismiss the modal the user just opened.
    if (previous !== null && previous !== contextKey) {
      dispatch(closeQueuePriorityModal());
    }
  }, [tenantId, serviceLine, dispatch]);

  // Open state is plain Redux, so without this the modal reopens by itself on the next mount
  // after a context jump. See `store/transient-state.ts` for the reload path.
  useEffect(
    () => () => {
      dispatch(closeQueuePriorityModal());
    },
    [dispatch]
  );

  if (!isOpen || !canPrioritize) return null;

  return (
    <Modal
      isOpen
      onClose={handleClose}
      size="6xl"
      isCentered
      scrollBehavior="inside"
      // Reopening refetches and resets the order, so a stray Escape or backdrop click would
      // discard an unsaved reorder with no warning. The close button stays available.
      closeOnEsc={!hasUnsavedOrder}
      closeOnOverlayClick={!hasUnsavedOrder}
    >
      <ModalOverlay />
      <ModalContent
        borderRadius="8px"
        data-testid="prioritize-work-modal"
        // Beats the global `.chakra-modal__body { padding: 20px !important }` leak from
        // shared-views/.../code-bench.vw-modal.scss: two-class specificity + !important.
        sx={{ '& .chakra-modal__body': { paddingTop: '0 !important' } }}
      >
        <ModalHeader px="20px" py="20px">
          <Flex justify="space-between" align="center">
            <Box
              as="h2"
              fontSize="24.19px"
              fontWeight={500}
              lineHeight="28px"
              textTransform="none"
            >
              {PRIORITIZE_WORK_MODAL_TITLE}
            </Box>
            <Flex gap={6} align="center">
              {/* QueuePriorityOrderView portals its Revert/Save buttons into this node. */}
              <Flex ref={setHeaderActionsNode} gap={6} align="center" />
              {/* Chakra pins this to the paper corner via styleConfig; static un-pins it
                  so it sits in the header row. */}
              <ModalCloseButton
                position="static"
                top="auto"
                insetEnd="auto"
                aria-label="Close Prioritize Work"
              />
            </Flex>
          </Flex>
        </ModalHeader>
        <ModalBody>
          <QueuePriorityHeaderActionsContext.Provider value={headerActionsNode}>
            <QueuePriorityOrderView />
          </QueuePriorityHeaderActionsContext.Provider>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

QueuePriorityModalContainer.displayName = 'QueuePriorityModalContainer';
export default QueuePriorityModalContainer;
